import dayjs from 'dayjs';

import type {
    SnowtrexAccommodation,
    SnowtrexAccommodationDate,
    SnowtrexAccommodationPrice,
    SnowtrexMonthlyPrices,
    SnowtrexSelection,
} from '$saga/views/Tour/VisibleDates/types';

const matchesPeriod = (
    period: SnowtrexAccommodationDate,
    selectedPax: number,
    selectedDuration: number | null,
    selectedMonth: string | null = null,
): boolean =>
    (selectedDuration === null || period.duration === selectedDuration) &&
    (selectedMonth === null ||
        dayjs(period.departureDate).isSame(dayjs(selectedMonth), 'month')) &&
    period.accommodations.some(
        (accommodation) => accommodation.pricesByPax[selectedPax] !== undefined,
    );

const getPrices = (
    period: SnowtrexAccommodationDate,
    selectedPax: number,
): SnowtrexAccommodationPrice[] =>
    period.accommodations.flatMap((accommodation) => {
        const price = accommodation.pricesByPax[selectedPax];

        return price === undefined ? [] : [price];
    });

export const getSnowtrexSelection = (
    period: SnowtrexAccommodationDate,
    accommodation: SnowtrexAccommodation,
    pax: number,
    adults: number = pax,
    children: number = 0,
): SnowtrexSelection => ({
    circuitDateId: period.id,
    departureDate: period.departureDate,
    returnDate: period.returnDate,
    duration: period.duration,
    accommodationId: accommodation.id,
    pax,
    adults,
    children,
    selectedPrice: accommodation.pricesByPax[pax]!,
});

export const getSnowtrexAccommodationPrice = (
    periods: SnowtrexAccommodationDate[],
    selection: SnowtrexSelection,
    pax: number,
): SnowtrexAccommodationPrice | null =>
    periods
        .find((period) => period.id === selection.circuitDateId)
        ?.accommodations.find(
            (accommodation) => accommodation.id === selection.accommodationId,
        )?.pricesByPax[pax] ?? null;

export const getSnowtrexMonthQuantile = (
    monthlyPrices: SnowtrexMonthlyPrices,
    month: string,
) => monthlyPrices[`${month}-01 00:00`]?.quantile;

export const getSnowtrexPriceForAside = (
    periods: SnowtrexAccommodationDate[],
    selectedPax: number,
    selectedDuration: number | null,
    selectedPrice: SnowtrexAccommodationPrice | null,
): number | null =>
    selectedPrice?.price ??
    getCheapestSnowtrexPrice(periods, selectedPax, selectedDuration);

export const getCheapestSnowtrexAccommodationPrice = (
    periods: SnowtrexAccommodationDate[],
    selectedPax: number,
    selectedDuration: number | null = null,
    selectedMonth: string | null = null,
): SnowtrexAccommodationPrice | null =>
    periods
        .filter((period) =>
            matchesPeriod(period, selectedPax, selectedDuration, selectedMonth),
        )
        .flatMap((period) => getPrices(period, selectedPax))
        .reduce(
            (min, price) =>
                min === null || price.price < min.price ? price : min,
            null as SnowtrexAccommodationPrice | null,
        );

export const getCheapestSnowtrexPrice = (
    periods: SnowtrexAccommodationDate[],
    selectedPax: number,
    selectedDuration: number | null = null,
    selectedMonth: string | null = null,
): number | null =>
    getCheapestSnowtrexAccommodationPrice(
        periods,
        selectedPax,
        selectedDuration,
        selectedMonth,
    )?.price ?? null;

export const getSnowtrexMonthPrices = (
    periods: SnowtrexAccommodationDate[],
    selectedPax: number,
    selectedDuration: number | null,
): { month: string; price: number }[] => {
    const pricesByMonth = new Map<string, number>();

    periods
        .filter((period) =>
            matchesPeriod(period, selectedPax, selectedDuration),
        )
        .forEach((period) => {
            const month = dayjs(period.departureDate).format('YYYY-MM');
            const price = getCheapestSnowtrexPrice(
                [period],
                selectedPax,
                selectedDuration,
            );

            if (price !== null) {
                pricesByMonth.set(
                    month,
                    Math.min(pricesByMonth.get(month) ?? price, price),
                );
            }
        });

    return Array.from(pricesByMonth, ([month, price]) => ({ month, price }));
};
